import React from "react";
import { Box, Chip, Drawer, Typography, alpha } from "@mui/material";
import { NotificationsNone as NotificationsNoneIcon } from "@mui/icons-material";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { DrawerHeader, DrawerFooter, EmptyState } from "../../../atoms";
import { COLORS, TYPO } from "../../../../styles/uiConstants";

const STATUS_META = {
  sent: { label: "Gönderildi", bg: alpha(COLORS.success, 0.08), fg: COLORS.successDark },
  delivered: { label: "Ulaştı", bg: alpha(COLORS.success, 0.08), fg: COLORS.successDark },
  failed: { label: "Başarısız", bg: alpha(COLORS.error, 0.08), fg: COLORS.errorDark },
  pending: { label: "Bekliyor", bg: alpha(COLORS.warning, 0.1), fg: COLORS.warningDark },
};

const responseText = (log) => {
  if (log.patientResponse == null || !log.responseFormat || log.responseFormat === "none") return null;
  if (typeof log.patientResponse === "boolean") return log.patientResponse ? "Evet" : "Hayır";
  return String(log.patientResponse);
};

/**
 * Gönderilmiş tek bildirim satırı: zaman · konu · durum; altında mesaj ve
 * hastanın cevabı. `px` çağıranın hizasıdır (editör bölümü 0, drawer 3).
 */
export function NotificationLogRow({ log, px = 0, showSubject = true }) {
  const meta = STATUS_META[String(log.status || "sent").toLowerCase()] || {
    label: log.status || "—",
    bg: COLORS.slate100,
    fg: COLORS.slate600,
  };
  const answer = responseText(log);
  return (
    <Box
      sx={{
        px,
        py: 1.25,
        display: "flex",
        flexDirection: "column",
        gap: 0.4,
        borderBottom: `1px solid ${COLORS.slate100}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
          {log.sentAt ? format(new Date(log.sentAt), "d MMM yyyy HH:mm", { locale: tr }) : "—"}
        </Typography>
        {showSubject ? (
          <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate800 }}>
            {log.subject || "—"}
          </Typography>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
        <Chip
          label={meta.label}
          size="small"
          sx={{ height: 22, fontSize: "0.65rem", fontWeight: 600, borderRadius: 999, bgcolor: meta.bg, color: meta.fg }}
        />
      </Box>
      {log.message ? (
        <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate600 }}>{log.message}</Typography>
      ) : null}
      {answer ? (
        <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
          Cevap:{" "}
          <Box component="span" sx={{ fontWeight: 600, color: COLORS.slate700 }}>
            {answer}
          </Box>
        </Typography>
      ) : null}
    </Box>
  );
}

/** Hastaya gönderilmiş tüm bildirimler (ayarı silinmiş olanlar dahil). */
export default function NotificationLogDrawer({ open, onClose, logs = [] }) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<NotificationsNoneIcon />}
        title="Gönderim Geçmişi"
        subtitle={logs.length ? `${logs.length} bildirim gönderildi` : "Henüz gönderim yok"}
        onClose={onClose}
      />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
        {logs.length === 0 ? (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", pb: 6 }}>
            <EmptyState
              variant="illustrated-sm"
              icon={<NotificationsNoneIcon />}
              title="Henüz bildirim gönderilmedi"
              description="Planlanan bildirimler saati geldiğinde ya da “Hemen Gönder” ile gönderildiğinde burada listelenir."
            />
          </Box>
        ) : (
          logs.map((log) => <NotificationLogRow key={log._id} log={log} px={3} />)
        )}
      </Box>
      <DrawerFooter onCancel={onClose} cancelLabel="Kapat" fullWidth />
    </Drawer>
  );
}
